import { HttpClient } from "@angular/common/http";
import { Inject } from "@angular/core";
import { HotToastService } from "@ngneat/hot-toast";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { Filter } from "../components/table/sortable-table/filter-form-group";
import { OdataList } from "../models/odataList";
import { AddResponseMessage, UpdateResponseMessage } from "../models/responseMessage";
import { AbstractService } from "./abstract.service";

export class BaseService<T, TList> extends AbstractService {
  constructor(
    protected override http: HttpClient,
    @Inject("BASE_URL") protected baseUrl: string,
    protected controller: string,
    protected toast: HotToastService | null = null
  ) {
    super(http);
  }

  getAllFilteredTable(filter: any): Observable<OdataList<TList>> {
    return this.getAllOdata("?" + this.serializeForQueryStringOdata(filter));
  }

  getById(id: number): Observable<T> {
    return this.get("/" + id);
  }

  add(item: T) {
    return this.post<T, AddResponseMessage>("", item);
  }

  update(item: T) {
    return this.put<T, UpdateResponseMessage>("", item);
  }

  toggleArchived(id: number) {
    return this.put<null, boolean>(`/${id}/disabled/toggle`, null);
  }

  hasAny(): Observable<boolean> {
    return this.get<boolean>("/any");
  }

  hasArchived() {
    return this.get<boolean>("/disabled/any");
  }

  getArchived(filter: Filter = new Filter()) {
    return this.getAllOdata("/disabled?" + this.serializeForQueryStringOdata(filter));
  }

  public canWriteId(id: number): Observable<boolean> {
    return this.get<boolean>("/canWrite/" + id);
  }

  getAll<ListType = TList>(action: string): Observable<ListType[]> {
    return this.get<ListType[]>(action);
  }

  getAllOdata<ListType = TList>(action?: string): Observable<OdataList<ListType>> {
    if (!action) action = "";
    return this.get<OdataList<ListType>>(action);
  }

  odataCount(action?: string): Observable<string> {
    return this.http
      .get(this.baseUrl + "odata/" + this.controller + "/$count", {
        responseType: "text",
      })
      .pipe(catchError(this.handleError<string>()));
  }

  /** HTTP actions */
  get<ReturnType = T>(action: string): Observable<ReturnType> {
    return this.http
      .get<ReturnType>(this.baseUrl + this.controller + action)
      .pipe(catchError((err) => this.reThrowError(err)));
  }
  post<SaveType = T, ReturnType = T>(action: string, item: SaveType) {
    return this.http
      .post<ReturnType>(this.baseUrl + this.controller + action, item)
      .pipe(catchError((err) => this.reThrowError(err)));
  }

  put<SaveType = T, ReturnType = T>(action: string, item?: SaveType) {
    return this.http
      .put<ReturnType>(this.baseUrl + this.controller + action, item)
      .pipe(catchError((err) => this.reThrowError(err)));
  }

  patch<SaveType = T, ReturnType = T>(action: string, body: SaveType) {
    return this.http
      .patch<ReturnType>(this.baseUrl + this.controller + action, body)
      .pipe(catchError((err) => this.reThrowError(err)));
  }

  deleteById(id: number) {
    return this.delete("/" + id);
  }

  protected delete(action: string) {
    return this.http
      .delete(this.baseUrl + this.controller + action)
      .pipe(catchError((err) => this.reThrowError(err)));
  }
}
